/**
 * melee.js — hand-to-hand combat: the move table and its geometry.
 *
 * Melee exists in exactly one situation: a fighter on foot whose board is gone.
 * That is the state the match used to have no answer for — you had thrown your
 * board, so had they, and colliding decided it on closing speed alone. This
 * turns that dead end into the fight.
 *
 * Everything here is a pure function of a player and an input frame, for the
 * same reason steering.js is: the online guest never simulates. It forwards
 * boolean key states and renders the host's snapshots, so a move has to be
 * *derivable* from booleans the guest can send, and the resulting state has to
 * be a few scalars the host can send back. Neither side ever exchanges a move
 * object — only which keys were down, and what the host decided that meant.
 *
 * Six moves out of two keys. The player learns two bindings, and the modifier
 * they are already holding — crouch, or being in the air — picks the variant.
 * That is deliberate: every key in both control schemes was already taken, and
 * the two that were free (the beam and the mode switch) are free *precisely*
 * because they are meaningless without a board. So melee reuses them, and the
 * two meanings can never collide.
 */

/** Melee-only health. Beams and boards still kill outright. */
export const MELEE_MAX_HP = 12;

/**
 * How long a fighter stays on the ground after being swept, in ms. Long enough
 * to be a real opening for the sweeper — this is the setup their whole low game
 * exists to create — but short enough that it is not simply a death sentence.
 */
export const KNOCKDOWN_TIME = 1500;

/** How long the flinch reaction plays after taking a hit, in ms. */
export const HURT_TIME = 220;

/**
 * Getting hit interrupts what you were doing. Without this a trade is decided
 * by nothing but who pressed first, and both fighters' attacks land regardless
 * — so blocking, spacing and timing all stop mattering.
 */
export const HURT_CANCELS_ATTACK = true;

/**
 * The moves.
 *
 *   time      total duration, ms. The fighter is committed for all of it.
 *   active    fraction of `time` during which the limb can connect. The gap
 *             before it is the windup that makes an attack readable and
 *             punishable; the gap after is the recovery that makes whiffing
 *             cost something.
 *   damage    HP removed, out of MELEE_MAX_HP.
 *   reach     how far the limb extends past the body edge, px.
 *   band      [top, bottom] of the strike relative to the victim's feet line,
 *             so a low attack genuinely passes under a high guard.
 *   push      knockback applied to whoever is hit, px/frame.
 *   knocksDown  puts them on the floor instead of staggering them.
 *
 * Tuned so the headline numbers are six punches or three kicks. Kicks hit twice
 * as hard and take half again as long to come out, which is the whole trade.
 */
export const MOVES = {
  punch: {
    time: 260, active: [0.25, 0.55], damage: 2, reach: 26, band: [-46, -22], push: 2.5,
  },
  punchlow: {
    time: 280, active: [0.28, 0.58], damage: 2, reach: 24, band: [-28, -4], push: 2,
  },
  kick: {
    time: 380, active: [0.30, 0.62], damage: 4, reach: 32, band: [-42, -14], push: 5,
  },
  // The sweep barely hurts. It is a setup, not a hit: it trades damage for the
  // knockdown, and the damage comes from whatever you do to them on the floor.
  // It also goes under a guard, which is most of the reason to throw one.
  sweep: {
    time: 430, active: [0.30, 0.66], damage: 1, reach: 34, band: [-16, 6], push: 3,
    knocksDown: true, beatsGuard: true,
  },
  // Committed and airborne, so it cannot be cancelled and cannot be repeated
  // without landing first. The hardest single hit in the game that is not
  // lethal, and it arrives over the top of a guard rather than into it.
  // The band reaches BELOW the attacker's feet, which no other move does. Every
  // band is measured from its owner's feet line, so a strike thrown from the air
  // rises with them — at head height it sailed clean over a standing opponent
  // from any jump worth making, and the one move that is supposed to answer a
  // guard could not reach the person guarding. The leg comes down as well as
  // forward now. Clearing them from the very top of a full jump still misses,
  // which is the part that should stay true.
  jumpkick: {
    time: 420, active: [0.15, 0.72], damage: 5, reach: 30, band: [-30, 14], push: 6,
    beatsGuard: true,
    // A dive, not a pose worn through an existing jump arc. Throwing it
    // discards whatever the jump was doing and commits the whole body to one
    // straight line forward and down.
    //
    // This is what makes it worth its 5 damage rather than merely the biggest
    // number on the table. As a free rider on a normal jump it was passive —
    // you leapt, you pressed kick, the arc carried you. As a dive it is a
    // decision: you pick a direction and a moment and you are locked into
    // both, you cannot steer, and if it misses you land in recovery with the
    // other fighter standing next to you.
    //
    //   speed     forward travel, px/frame. Above the on-foot run so diving in
    //             genuinely closes distance rather than drifting.
    //   fall      descent, px/frame. Together these two set the angle the
    //             renderer pitches the body to (~38°).
    //   recovery  ms of being unable to act after touching down, whether or
    //             not it connected. The price of committing.
    dive: { speed: 9.5, fall: 7, recovery: 240 },
  },
  // Finishers on a downed opponent. Both strike the floor, so their band sits
  // at ground level where a standing fighter's body box does not reach.
  stomp: {
    time: 400, active: [0.35, 0.62], damage: 4, reach: 20, band: [-12, 10], push: 0,
  },
  pound: {
    time: 340, active: [0.32, 0.62], damage: 3, reach: 20, band: [-12, 10], push: 0,
  },
};

/** Reach of the longest move, so callers can size a proximity test. */
export const MAX_REACH = Math.max(...Object.values(MOVES).map((m) => m.reach));

/**
 * How close you have to stand to a downed fighter for your attacks to become
 * finishers rather than ordinary swings at head height.
 */
export const FINISH_RANGE = 30;

/**
 * Knockback applied to a fighter who blocks a hit, as a fraction of the move's
 * normal push. They keep their feet and their health, but they lose ground —
 * which in a game about pushing someone across seven screens is the real cost
 * of turtling, and the reason a guard needs no stamina meter to be balanced.
 */
export const GUARD_PUSH_FRAC = 0.6;

/**
 * Is this fighter actively holding a guard?
 *
 * Guarding is a held state, not a move, so it has to be true on the frame the
 * strike resolves rather than on the frame a key went down. Throwing your own
 * attack drops it: the arms that were covering your head are the ones you just
 * punched with.
 *
 * @param {object} p Player state.
 * @returns {boolean}
 */
export function isGuarding(p) {
  return !!p.guarding
    && !p.attack
    && !p.onBoard
    && p.downTime <= 0
    && p.hurtTime <= 0
    && p.stunned <= 0;
}

/**
 * Does a guard stop this particular strike?
 *
 * The answer is what makes melee a decision rather than a race to swing. A
 * guard beats the safe attacks — punch, low punch, kick — so mashing into
 * someone who is covering up achieves nothing. The two moves that get through
 * are the two committed ones: the sweep goes under it and the flying kick comes
 * over it, and both are slow enough to be punished if they are read.
 *
 * A guard also only covers the way the fighter is facing, so getting behind
 * someone beats it for free.
 *
 * @param {object} victim
 * @param {object} attacker
 * @param {object} move Entry from MOVES.
 * @returns {boolean} true if the hit is blocked entirely.
 */
export function guardStops(victim, attacker, move) {
  if (!isGuarding(victim)) return false;
  if (move.beatsGuard) return false;
  const attackerIsAhead = attacker.x > victim.x;
  return attackerIsAhead === victim.facingRight;
}

/**
 * Can this fighter act at all right now?
 *
 * Melee is only available on foot with the board gone — while riding or aiming
 * the hands and feet are busy, and that separation is what keeps the two halves
 * of the game from overlapping.
 *
 * @param {object} p Player state.
 * @returns {boolean}
 */
export function canFight(p) {
  return !p.onBoard
    && p.vaporizing <= 0
    && p.stunned <= 0
    && p.downTime <= 0
    && p.hurtTime <= 0;
}

/**
 * Pick the move an input frame is asking for, or '' for none.
 *
 * The modifier does the work: guarding turns both attacks into their low
 * variants, leaving the ground turns a kick into a flying one, and standing
 * over someone already down turns both into finishers.
 *
 * The low attacks come off the GUARD button rather than a held direction —
 * same gesture as every fighting game's low attack, but on a button, so it does
 * not fight the stick that is also steering.
 *
 * @param {object} input     Boolean input frame (needs punch/kick/down).
 * @param {object} ctx
 * @param {boolean} ctx.airborne   Fighter has left the ground.
 * @param {boolean} ctx.overDowned Standing within reach of a downed opponent.
 * @returns {string} key into MOVES, or '' if no attack was requested.
 */
export function chooseMove(input, { airborne, overDowned }) {
  if (!input.punch && !input.kick) return '';

  // Someone on the floor is finished off, not boxed with. Checked before the
  // airborne branch so coming down on them still lands the stomp.
  if (overDowned) return input.kick ? 'stomp' : 'pound';

  if (input.kick) {
    if (airborne) return 'jumpkick';
    return input.guard ? 'sweep' : 'kick';
  }
  // There is no air punch: leaving the ground commits you to the flying kick,
  // so jumping in is a choice with one answer rather than a free mixup.
  if (airborne) return '';
  return input.guard ? 'punchlow' : 'punch';
}

/**
 * Is this fighter mid-dive?
 *
 * True for the whole airborne part of a diving move, which is what both the
 * physics (hold the line, ignore gravity and friction) and the renderer (pitch
 * the body along it) key off. Landing ends it, so it is deliberately false
 * during the recovery that follows.
 *
 * @param {object} p Player state.
 * @returns {boolean}
 */
export function isDiving(p) {
  const move = MOVES[p.attack];
  return !!(move && move.dive) && !p.grounded && !p.onBoard;
}

/**
 * The angle a diving body should be pitched to, in radians.
 *
 * Read off the fighter's actual velocity rather than the move's nominal
 * numbers, so if anything ever perturbs the trajectory the drawing still points
 * the way they are really travelling.
 *
 * @param {object} p Player state.
 * @returns {number} forward-down pitch, positive meaning nose down.
 */
export function diveAngle(p) {
  return Math.atan2(Math.max(0, p.vy), Math.max(1, Math.abs(p.vx)));
}

/**
 * Is the current attack in its active frames — the window where it connects?
 *
 * @param {object} p Player state (attack, attackTime).
 * @returns {boolean}
 */
export function isStriking(p) {
  const move = MOVES[p.attack];
  if (!move) return false;
  // attackTime counts DOWN, so elapsed is the complement.
  const progress = 1 - p.attackTime / move.time;
  return progress >= move.active[0] && progress <= move.active[1];
}

/**
 * The box the striking limb sweeps, in world coordinates.
 *
 * Projected forward from the edge of the body rather than from its centre, so
 * reach means the same thing whichever way the fighter faces.
 *
 * @param {object} p Player state.
 * @param {number} bodyHalfWidth Half-width of the body box, px.
 * @returns {{left: number, right: number, top: number, bottom: number}|null}
 */
export function strikeBox(p, bodyHalfWidth) {
  const move = MOVES[p.attack];
  if (!move) return null;
  const dir = p.facingRight ? 1 : -1;
  const front = p.x + dir * bodyHalfWidth;
  const tip = front + dir * move.reach;
  return {
    left: Math.min(front, tip),
    right: Math.max(front, tip),
    top: p.y + move.band[0],
    bottom: p.y + move.band[1],
  };
}

/**
 * The pose that should be drawn for a fighter's melee state, or '' if melee
 * has nothing to say about it and the normal locomotion poses apply.
 *
 * Ordered by what overrides what: being on the floor beats being hurt, which
 * beats swinging, which beats holding a crouch.
 *
 * @param {object} p Player state.
 * @returns {string} pose name from the sprite library, or ''.
 */
export function meleePose(p) {
  if (p.downTime > 0) return 'downed';
  if (p.hurtTime > 0) return 'stagger';
  if (p.attack) return p.attack;
  if (p.guarding) return 'guard';
  return '';
}
